import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { loginFormStyles, loginFormUtils, loginFormAnimations } from '../styles/components/LoginForm.styles';
import { loginService } from '../services/loginService';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/ui/ToastManager';
import { Eye, EyeOff } from 'lucide-react';
import { 
  validateEmail,
  validatePassword
} from '../utils/formValidation';
import { ROUTES, NAVIGATION_OPTIONS } from '../config/routes';
import { errorLogger } from '../utils/errorLogger';

/**
 * ログインフォームコンポーネント
 * Firebase認証を使用したメール・パスワードログイン
 */
const LoginForm = ({ onLoginSuccess }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showError } = useToast();
  const { login } = useAuth(); 

  // フォーム状態管理
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  // UI状態管理
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [focusedField, setFocusedField] = useState('');

  // リアルタイムバリデーション用のエラー状態
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  /**
   * 入力フィールドの変更ハンドラー
   */
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // パスワードフィールドの場合、自動的に trim()
    const finalValue = name === 'password' ? value.trim() : value;
    
    // フォームデータを更新
    setFormData(prev => ({ ...prev, [name]: finalValue }));

    // リアルタイムバリデーション（共通関数を使用）
    if (name === 'email') {
      setEmailError(validateEmail(finalValue));
    }
    if (name === 'password') {
      // システム共通のパスワード要件（8文字以上、英大文字、英小文字、数字含む）
      setPasswordError(validatePassword(finalValue));
    }
    
    // 全般的なエラーをクリア（ユーザーが入力を続けている場合）
    if (errors.general) {
      setErrors(prev => ({ ...prev, general: '' }));
    }
    
    // 該当フィールドのエラーをクリア
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  /**
   * フィールドフォーカス管理
   */
  const handleFocus = (fieldName) => {
    setFocusedField(fieldName);
  };

  const handleBlur = () => {
    setFocusedField('');
  };

  /**
   * パスワード表示/非表示の切り替え
   */
  const togglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
  };

  /**
   * フォーム送信前のバリデーション処理
   */
  const validateForm = () => {
    const newErrors = {};
    
    // メールアドレスのバリデーション（共通関数を使用）
    const emailValidationError = validateEmail(formData.email);
    if (emailValidationError) {
      newErrors.email = emailValidationError;
    }
    
    // パスワードのバリデーション（システム共通要件：8文字以上、英大文字、英小文字、数字含む）
    const passwordValidationError = validatePassword(formData.password);
    if (passwordValidationError) {
      newErrors.password = passwordValidationError;
    }
    
    // エラーがある場合は状態を更新
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return false;
    }

    return true;
  };

  /**
   * ログイン実行処理
   */
  const executeLogin = async () => {

  // 登録時は小文字に正規化して保存されるため、ログイン時も同じ形に揃える
  const normalizedEmail = formData.email.toLowerCase().trim();

  try {
    // バックエンドAPIでログイン
    const result = await loginService.loginWithEmailAndPassword(
      normalizedEmail,
      formData.password.trim()
    );

    if (!result.success) {
      errorLogger.log('[LOGIN FORM]', 'Login failed:', result.error);
      setErrors({ general: result.error || 'ログインに失敗しました' });
      return null;
    }

    // ログイン成功時、最小限の情報を返す
    // /api/meで詳細情報は後で取得する
    return {
      email: normalizedEmail,
      password: formData.password,
      loginSuccess: true,
      timestamp: Date.now()
    };

  } catch (error) {
    errorLogger.logError('[LOGIN FORM]', error);
    setErrors({ general: error.message || 'ログインに失敗しました' });
    return null;
  }
};

const handleLoginSuccess = async (loginData) => {

  // ログインデータの最小限の検証
  if (!loginData || !loginData.email) {
    errorLogger.log('[LOGIN FORM]', 'Invalid login data');
    showError('ログイン処理に失敗しました');
    return;
  }

  try {
    // AuthContextに最小限の情報でログインを通知
    // 詳細な会員情報は後で/api/meから取得される
    await login(loginData);

    // コールバック処理が指定されている場合
    if (onLoginSuccess) {
      onLoginSuccess(loginData);
      return;
    }

    // ホームページへリダイレクト（オープンリダイレクト防止）
    const requestedPath = location.state?.from;
    const isSafeRedirect =
      typeof requestedPath === 'string' &&
      requestedPath.startsWith('/') &&
      !requestedPath.startsWith('//') &&
      !requestedPath.includes('://');
    const redirectTo = isSafeRedirect ? requestedPath : ROUTES.HOME;
    navigate(redirectTo, NAVIGATION_OPTIONS.REPLACE);

  } catch (error) {
    errorLogger.logError('[LOGIN FORM] Login state update failed', error);
    showError('ログイン処理中にエラーが発生しました');
  }
};

  /**
   * フォーム送信処理
   */
  const handleSubmit = async (e) => {
  e.preventDefault();
  
  // 重複送信防止
  if (isLoading) return;
  
  setIsLoading(true);
  setErrors({});

  try {
    // バリデーション
    if (!validateForm()) {
      setIsLoading(false);
      return;
    }

    // ログイン処理
    const result = await executeLogin();
    
    if (result) {
      // handleLoginSuccessを呼び出す
      handleLoginSuccess(result);
    }
  } catch (error) {
    errorLogger.logError('[LOGIN FORM]', error);
    setErrors({ general: error.message || 'ログインに失敗しました' });
  } finally {
    setIsLoading(false);
  }
};

  /**
   * 新規登録画面への遷移
   */
  const handleNavigateToRegister = () => {
    navigate(ROUTES.TERMS);
  };

  /**
   * パスワードリセット画面への遷移
   */
  const handleNavigateToPasswordReset = () => {
    navigate(ROUTES.FORGOT_PASSWORD);
  };

  // フォームの有効性チェック
  const isFormValid = formData.email.trim() !== '' && 
                      formData.password.trim() !== '' && 
                      !emailError && 
                      !passwordError;
                    
  return (
    <>
      <style>{loginFormAnimations}</style>
      <div style={loginFormStyles.pageContainer}>
        <div style={loginFormStyles.container}>
          <h1 style={loginFormStyles.title}>ログイン</h1>
          
          <form onSubmit={handleSubmit} style={loginFormStyles.form} noValidate> 
            {/* メールアドレス入力 */}
            <div style={loginFormStyles.fieldGroup}>
              <label style={loginFormStyles.label}>
                <span>メールアドレス</span>
                <span style={loginFormStyles.required}>*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                onFocus={() => handleFocus('email')}
                onBlur={handleBlur}
                style={loginFormUtils.getInputStyle(!!(errors.email || emailError), focusedField === 'email')}
                placeholder="taro@example.com"
                disabled={isLoading}
                autoComplete="email"
              />
              {/* リアルタイムバリデーションエラーまたは送信時エラーを表示 */}
              {(emailError || errors.email) && (
                <div style={loginFormUtils.getErrorMessageStyle()}>
                  {emailError || errors.email}
                </div>
              )}
            </div>

            {/* パスワード入力 */}
            <div style={loginFormStyles.fieldGroup}>
              <label style={loginFormStyles.label}>
                <span>パスワード</span>
                <span style={loginFormStyles.required}>*</span>
              </label>
              <div style={loginFormStyles.passwordContainer}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  onFocus={() => handleFocus('password')}
                  onBlur={handleBlur}
                  style={loginFormUtils.getInputStyle(!!(errors.password || passwordError), focusedField === 'password')}
                  placeholder="パスワードを入力"
                  disabled={isLoading}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  style={{
                    ...loginFormStyles.passwordToggle,
                    color: '#666',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  disabled={isLoading}
                  aria-label={showPassword ? 'パスワードを非表示' : 'パスワードを表示'}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {/* リアルタイムバリデーションエラーまたは送信時エラーを表示 */}
              {(passwordError || errors.password) && (
                <div style={loginFormUtils.getErrorMessageStyle()}>
                  {passwordError || errors.password}
                </div>
              )}
            </div>

            {/* 全般的なエラー表示 */}
            {errors.general && (
              <div style={loginFormUtils.getErrorMessageStyle()}>
                {errors.general}
              </div>
            )}

            {/* ログインボタン */}
            <button
              type="submit"
              disabled={!isFormValid || isLoading}
              style={loginFormUtils.getButtonStyle(isLoading, !isFormValid || isLoading)}
              onMouseEnter={(e) => {
                if (!isLoading && isFormValid) {
                  Object.assign(e.currentTarget.style, loginFormStyles.loginButtonHover);
                }
              }}
              onMouseLeave={(e) => {
                if (!isLoading && isFormValid) {
                  e.currentTarget.style.backgroundColor = loginFormStyles.loginButton.backgroundColor;
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }
              }}
            >
              {isLoading && <div style={loginFormStyles.loadingSpinner}></div>}
              <span>{isLoading ? 'ログイン中...' : 'ログイン'}</span>
            </button>
          </form>

          {/* パスワードを忘れた場合のリンク */}
          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <button
              type="button"
              onClick={handleNavigateToPasswordReset}
              style={{
                ...loginFormStyles.link,
                background: 'none',
                border: 'none'
              }}
              onMouseEnter={(e) => {
                Object.assign(e.target.style, loginFormStyles.linkHover);
              }}
              onMouseLeave={(e) => {
                e.target.style.color = loginFormStyles.link.color;
                e.target.style.textDecoration = 'none';
              }}
            >
              パスワードをお忘れの方はこちら
            </button>
          </div>

          {/* 区切り線 */}
          <div style={loginFormStyles.divider}>
            <div style={loginFormStyles.dividerLine}></div>
            <span style={loginFormStyles.dividerText}>または</span>
          </div>

          {/* 新規登録へのリンク */}
          <div style={loginFormStyles.registerContainer}>
            <p style={loginFormStyles.registerText}>
              まだアカウントをお持ちでない方
            </p>
            <button
              type="button"
              onClick={handleNavigateToRegister}
              style={{
                ...loginFormStyles.registerButton,
                background: 'none',
                border: 'none'
              }}
              onMouseEnter={(e) => {
                Object.assign(e.target.style, loginFormStyles.linkHover);
              }}
              onMouseLeave={(e) => {
                e.target.style.color = loginFormStyles.registerButton.color;
                e.target.style.textDecoration = 'none';
              }}
            >
              新規会員登録はこちら
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginForm;